import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
  type UserCredential,
} from "firebase/auth";
import { auth } from "./firebase";
import { call, CALLABLES } from "./callables";
import { AppError } from "./errors";

/**
 * Signing in.
 *
 * Two kinds of identifier reach this form. A teacher added by hand has an email
 * address. A teacher who arrived in a class-list import has a username on a
 * printed slip and no address at all — school exports do not carry staff
 * addresses, and demanding one would have blocked the import.
 *
 * An `@` decides which. A username is resolved by the backend, which answers
 * uniformly whether or not the name exists: an unknown one comes back as an
 * address in a reserved domain, so the sign-in fails exactly as a wrong
 * password does. Without that, this form would enumerate a school's roster.
 */

export function looksLikeEmail(identifier: string): boolean {
  return identifier.includes("@");
}

export async function resolveIdentifier(identifier: string): Promise<string> {
  const trimmed = identifier.trim();
  if (looksLikeEmail(trimmed)) return trimmed.toLowerCase();

  const { email } = await call<{ username: string }, { email: string }>(
    CALLABLES.resolveUsername,
    { username: trimmed }
  );
  return email;
}

/**
 * One failure message for every reason.
 *
 * Wrong password, unknown address, disabled account and a rate-limited client
 * are all the same sentence. Telling them apart tells whoever is probing the
 * form which accounts exist, and the person who genuinely mistyped is no better
 * served by the distinction.
 */
export async function signIn(identifier: string, password: string): Promise<UserCredential> {
  const email = await resolveIdentifier(identifier);
  try {
    return await signInWithEmailAndPassword(auth(), email, password);
  } catch {
    throw new AppError("error.auth.invalidCredentials", "unauthenticated");
  }
}

/**
 * Replacing the printed password.
 *
 * Firebase first, then the flag. If the flag were cleared first and the update
 * failed, the user would keep the password that was printed on paper with
 * nothing left to prompt them about it.
 */
export async function completePasswordChange(password: string): Promise<void> {
  const current = auth().currentUser;
  if (!current) throw new AppError("error.auth.required", "unauthenticated");

  await updatePassword(current, password);
  await call(CALLABLES.completePasswordChange);
}

export type ResetOutcome = "sent" | "noEmailAddress";

/**
 * A forgotten password.
 *
 * Only an account with a real email address can be reset this way; a
 * username-only account has nowhere to send the link. That is not a dead end —
 * the owner reissues credentials from the People page in a few seconds — so it
 * returns an outcome the form can explain rather than an error.
 *
 * A real address is answered with "sent" whether or not an account exists.
 */
export async function requestPasswordReset(identifier: string): Promise<ResetOutcome> {
  const trimmed = identifier.trim();
  if (!looksLikeEmail(trimmed)) return "noEmailAddress";

  try {
    await sendPasswordResetEmail(auth(), trimmed.toLowerCase());
  } catch {
    // Deliberately swallowed. `auth/user-not-found` here would confirm which
    // addresses hold accounts.
  }
  return "sent";
}
